import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withKeys as _withKeys, withModifiers as _withModifiers, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "gs-box",
  role: "dialog",
  "aria-label": "Busca global"
}
const _hoisted_2 = { class: "gs-input-row" }
const _hoisted_3 = ["onKeydown"]
const _hoisted_4 = {
  key: 1,
  class: "gs-shortcut"
}
const _hoisted_5 = { class: "gs-body" }
const _hoisted_6 = {
  key: 0,
  class: "gs-state"
}
const _hoisted_7 = {
  key: 1,
  class: "gs-state gs-state--hint"
}
const _hoisted_8 = {
  key: 2,
  class: "gs-state"
}
const _hoisted_9 = {
  key: 3,
  class: "gs-results"
}
const _hoisted_10 = ["onClick", "onMouseenter"]
const _hoisted_11 = { class: "gs-avatar" }
const _hoisted_12 = { class: "gs-item-body" }
const _hoisted_13 = { class: "gs-item-title" }
const _hoisted_14 = { class: "gs-item-sub" }
const _hoisted_15 = {
  key: 0,
  class: "gs-item-extra"
}
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = {
  key: 2,
  class: "gs-item-extra-fluxo"
}
const _hoisted_19 = ["onClick", "onMouseenter"]
const _hoisted_20 = { class: "gs-avatar gs-avatar--contact" }
const _hoisted_21 = { class: "gs-item-body" }
const _hoisted_22 = { class: "gs-item-title" }
const _hoisted_23 = { class: "gs-item-sub" }

import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { globalSearchOpen, closeGlobalSearch } from '@/composables/useGlobalSearch'
import { useFloatChats } from '@/composables/useFloatChats'
import { fluxoLabel } from '@/composables/useHelpers'
import { fmtPhone, fmtDoc } from '@/utils/contactsFormat'
import { fetchConversationById } from '@/services/conversationService'
import { isFluxoHidden } from '@/composables/useHiddenFluxos'
import { useConversationStore } from '@/composables/useConversationStore'
import type { Conversation } from '@/types/dashboard'

const CHATWOOT_BASE = '/chatwoot-api'


export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

const open  = globalSearchOpen
const store  = useStore()
const router = useRouter()
const authUser = computed(() => store.getters.authUser)
const { open: openFloatChat } = useFloatChats()
const { conversations: cachedConversations } = useConversationStore()

const query        = ref('')
const loading      = ref(false)
const focusedIndex = ref(0)
const inputRef     = ref<HTMLInputElement | null>(null)
const results      = ref<{ conversations: Conversation[], contacts: any[] }>({ conversations: [], contacts: [] })

const hasResults  = computed(() => results.value.conversations.length > 0 || results.value.contacts.length > 0)
const totalItems  = computed(() => results.value.conversations.length + results.value.contacts.length)

const statusLabel: Record<string, string> = {
  open:     'Atendimento',
  pending:  'Na fila',
  resolved: 'Resolvida',
  snoozed:  'Pausada',
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(open, (val) => {
  if (val) {
    query.value = ''
    results.value = { conversations: [], contacts: [] }
    focusedIndex.value = 0
    nextTick(() => inputRef.value?.focus())
  }
})

watch(query, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  focusedIndex.value = 0
  if (!val || val.length < 2) {
    results.value = { conversations: [], contacts: [] }
    loading.value = false
    return
  }
  loading.value = true
  debounceTimer = setTimeout(() => doSearch(val), 300)
})

let searchSeq = 0

// Protocolo = yyyyMMdd (8 dígitos) + convId com zero-fill de 7 dígitos — mesmo
// formato gerado por protocol() em utils/avaliacoesFormat.ts. É só uma convenção
// visual do front (não existe busca por "protocolo" na API do Chatwoot), então
// aqui a gente detecta o padrão e resolve direto pro id real da conversa.
const PROTOCOL_RE = /^\d{15}$/

function protocolToConvId(q: string): number | null {
  const trimmed = q.trim()
  if (!PROTOCOL_RE.test(trimmed)) return null
  return parseInt(trimmed.slice(8), 10)
}

async function doSearch(q: string) {
  const user = authUser.value
  if (!user) { loading.value = false; return }
  const seq = ++searchSeq
  try {
    const protocolConvId = protocolToConvId(q)
    let convIds: number[]
    let contacts: any[]

    if (protocolConvId !== null) {
      convIds = [protocolConvId]
      contacts = []
    } else {
      const res = await fetch(
        `${CHATWOOT_BASE}/api/v1/accounts/${user.account_id}/search?q=${encodeURIComponent(q)}&page=1`,
        { headers: { 'api_access_token': user.access_token } }
      )
      if (!res.ok) throw new Error()
      const data = await res.json()
      convIds  = (data.payload?.conversations ?? []).slice(0, 5).map((c: any) => c.id)
      contacts = (data.payload?.contacts      ?? []).slice(0, 5)
    }

    // O endpoint de busca do Chatwoot devolve um objeto de conversa "raso" (sem
    // meta.sender/team nem custom_attributes) — precisa da versão completa pra
    // ter nome do contato, telefone, cpf/cnpj e fluxo. Se a conversa já está em
    // memória (conversations.value, alimentado pelo polling do webhook n8n em
    // useDashboardEngine.ts), usa direto de lá — sem chamada nenhuma ao
    // Chatwoot, e sem nem tocar na rede pra uma conversa do Fluxo Geral que já
    // sabemos, pelo cache, que vai ser descartada. Só cai pra
    // fetchConversationById() (Chatwoot direto) quando a conversa não está no
    // cache — ex.: resolvida há muito tempo, fora do período carregado.
    const hydrated = await Promise.all(
      convIds.map(id => cachedConversations.value.find(c => c.id === id) ?? fetchConversationById(id, user.access_token))
    )
    if (seq !== searchSeq) return // resposta de uma busca já obsoleta (query mudou nesse meio-tempo)

    // Fluxo Geral (fluxo_id 1, robô/Typebot/IA) fica sempre escondido em todo
    // o dashboard (ver useHiddenFluxos.ts) — inclusive aqui, mesmo achando a
    // conversa por protocolo exato: se ainda está com o robô, não deve
    // aparecer pro agente em nenhuma tela.
    results.value = {
      conversations: hydrated.filter((c): c is Conversation => c !== null && !isFluxoHidden(c.fluxo_id)),
      contacts,
    }
  } catch {
    if (seq !== searchSeq) return
    results.value = { conversations: [], contacts: [] }
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

function close() { closeGlobalSearch() }

function openConv(conv: Conversation) {
  // Popup flutuante (FloatChatWindow via useFloatChats) — não o painel encaixado do
  // ConvDrawer, que fica invisível se o agente estiver no modo "agente" do trilho.
  openFloatChat(conv)
  close()
}

function openContact(contact: any) {
  router.push({ path: '/contacts', query: { q: contact.name } })
  close()
}

function moveFocus(dir: number) {
  if (totalItems.value === 0) return
  focusedIndex.value = (focusedIndex.value + dir + totalItems.value) % totalItems.value
}

function selectFocused() {
  if (!hasResults.value) return
  const convCount = results.value.conversations.length
  if (focusedIndex.value < convCount) {
    openConv(results.value.conversations[focusedIndex.value])
  } else {
    openContact(results.value.contacts[focusedIndex.value - convCount])
  }
}

function initials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function convDisplayName(conv: Conversation): string {
  const name = conv.contact?.name?.trim()
  if (name && name !== '—') return name
  const phone = conv.contact?.phone?.trim()
  if (phone) return phone
  return `Conversa #${conv.id}`
}

function convPhone(conv: Conversation): string | null {
  const phone = conv.contact?.phone?.trim()
  return phone ? fmtPhone(phone) : null
}

// cpf/cnpj ficam em custom_attributes da CONVERSA (não do contato) — já
// normalizados em cpf/cnpj pelo mapRawConversation (chatwootRealtimeTranslator.ts).
function convDoc(conv: Conversation): string | null {
  if (conv.cnpj) return `CNPJ ${fmtDoc(conv.cnpj)}`
  if (conv.cpf)  return `CPF ${fmtDoc(conv.cpf)}`
  return null
}

function convFluxo(conv: Conversation): string | null {
  return conv.fluxo_id ? fluxoLabel(conv.fluxo_id) : null
}

function convInitials(conv: Conversation): string {
  const name = conv.contact?.name?.trim()
  if (name && name !== '—') return initials(name)
  const phone = conv.contact?.phone?.trim()
  if (phone) return phone.slice(-2)
  return '#'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "gs-fade" }, {
      default: _withCtx(() => [
        (_unref(open))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "gs-overlay",
              onMousedown: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _cache[5] || (_cache[5] = _createElementVNode("svg", {
                    class: "gs-search-icon",
                    width: "16",
                    height: "16",
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2.5",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round"
                  }, [
                    _createElementVNode("circle", {
                      cx: "11",
                      cy: "11",
                      r: "8"
                    }),
                    _createElementVNode("line", {
                      x1: "21",
                      y1: "21",
                      x2: "16.65",
                      y2: "16.65"
                    })
                  ], -1)),
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "inputRef",
                    ref: inputRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                    class: "gs-input",
                    placeholder: "Buscar protocolos, contatos...",
                    autocomplete: "off",
                    spellcheck: "false",
                    onKeydown: [
                      _withKeys(close, ["esc"]),
                      _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (moveFocus(1)), ["prevent"]), ["down"])),
                      _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveFocus(-1)), ["prevent"]), ["up"])),
                      _withKeys(_withModifiers(selectFocused, ["prevent"]), ["enter"])
                    ]
                  }, null, 40, _hoisted_3), [
                    [_vModelText, query.value]
                  ]),
                  (query.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "gs-clear",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => {query.value = ''; inputRef.value?.focus()})
                      }, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("svg", {
                          width: "14",
                          height: "14",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2.5",
                          "stroke-linecap": "round"
                        }, [
                          _createElementVNode("line", {
                            x1: "18",
                            y1: "6",
                            x2: "6",
                            y2: "18"
                          }),
                          _createElementVNode("line", {
                            x1: "6",
                            y1: "6",
                            x2: "18",
                            y2: "18"
                          })
                        ], -1)
                      ]))]))
                    : (_openBlock(), _createElementBlock("kbd", _hoisted_4, "Ctrl K"))
                ]),
                _createElementVNode("div", _hoisted_5, [
                  (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", { class: "gs-spinner" }, null, -1),
                        _createTextVNode(" Buscando... ", -1)
                      ]))]))
                    : (!query.value || query.value.length < 2)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("svg", {
                            width: "32",
                            height: "32",
                            viewBox: "0 0 24 24",
                            fill: "none",
                            stroke: "currentColor",
                            "stroke-width": "1.5",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, [
                            _createElementVNode("circle", {
                              cx: "11",
                              cy: "11",
                              r: "8"
                            }),
                            _createElementVNode("line", {
                              x1: "21",
                              y1: "21",
                              x2: "16.65",
                              y2: "16.65"
                            })
                          ], -1),
                          _createElementVNode("span", null, "Digite para buscar protocolos ou contatos", -1)
                        ]))]))
                      : (!hasResults.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _cache[8] || (_cache[8] = _createTextVNode(" Nenhum resultado para \"", -1)),
                            _createElementVNode("strong", null, _toDisplayString(query.value), 1),
                            _cache[9] || (_cache[9] = _createTextVNode("\" ", -1))
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            (results.value.conversations.length)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _cache[11] || (_cache[11] = _createElementVNode("div", { class: "gs-group-label" }, [
                                    _createElementVNode("svg", {
                                      width: "11",
                                      height: "11",
                                      viewBox: "0 0 24 24",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "2.5",
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round"
                                    }, [
                                      _createElementVNode("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
                                    ]),
                                    _createTextVNode(" Conversas ")
                                  ], -1)),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value.conversations, (conv, i) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                      key: 'cv' + conv.id,
                                      class: _normalizeClass(["gs-item", { 'gs-item--focus': focusedIndex.value === i }]),
                                      onClick: ($event: any) => (openConv(conv)),
                                      onMouseenter: ($event: any) => (focusedIndex.value = i)
                                    }, [
                                      _createElementVNode("span", _hoisted_11, _toDisplayString(convInitials(conv)), 1),
                                      _createElementVNode("span", _hoisted_12, [
                                        _createElementVNode("span", _hoisted_13, _toDisplayString(convDisplayName(conv)), 1),
                                        _createElementVNode("span", _hoisted_14, [
                                          _createTextVNode(" #" + _toDisplayString(conv.id) + " ", 1),
                                          (conv.team?.name)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createTextVNode(" · " + _toDisplayString(conv.team.name), 1)
                                              ], 64))
                                            : _createCommentVNode("", true),
                                          _createElementVNode("span", {
                                            class: _normalizeClass(["gs-badge", 'gs-badge--' + conv.status])
                                          }, _toDisplayString(statusLabel[conv.status] || conv.status), 3)
                                        ]),
                                        (convPhone(conv) || convDoc(conv) || convFluxo(conv))
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                                              (convPhone(conv))
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(convPhone(conv)), 1))
                                                : _createCommentVNode("", true),
                                              (convDoc(conv))
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(convDoc(conv)), 1))
                                                : _createCommentVNode("", true),
                                              (convFluxo(conv))
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(convFluxo(conv)), 1))
                                                : _createCommentVNode("", true)
                                            ]))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _cache[10] || (_cache[10] = _createElementVNode("svg", {
                                        class: "gs-item-arrow",
                                        width: "14",
                                        height: "14",
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        stroke: "currentColor",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      }, [
                                        _createElementVNode("polyline", { points: "9 18 15 12 9 6" })
                                      ], -1))
                                    ], 42, _hoisted_10))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true),
                            (results.value.contacts.length)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createElementVNode("div", {
                                    class: "gs-group-label",
                                    style: _normalizeStyle(results.value.conversations.length ? 'margin-top:10px' : '')
                                  }, [...(_cache[12] || (_cache[12] = [
                                    _createElementVNode("svg", {
                                      width: "11",
                                      height: "11",
                                      viewBox: "0 0 24 24",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "2.5",
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round"
                                    }, [
                                      _createElementVNode("path", { d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" }),
                                      _createElementVNode("circle", {
                                        cx: "12",
                                        cy: "7",
                                        r: "4"
                                      })
                                    ], -1),
                                    _createTextVNode(" Contatos ", -1)
                                  ]))], 4),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value.contacts, (contact, i) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                      key: 'ct' + contact.id,
                                      class: _normalizeClass(["gs-item", { 'gs-item--focus': focusedIndex.value === results.value.conversations.length + i }]),
                                      onClick: ($event: any) => (openContact(contact)),
                                      onMouseenter: ($event: any) => (focusedIndex.value = results.value.conversations.length + i)
                                    }, [
                                      _createElementVNode("span", _hoisted_20, _toDisplayString(initials(contact.name)), 1),
                                      _createElementVNode("span", _hoisted_21, [
                                        _createElementVNode("span", _hoisted_22, _toDisplayString(contact.name), 1),
                                        _createElementVNode("span", _hoisted_23, _toDisplayString(contact.phone_number || contact.email || '—'), 1)
                                      ]),
                                      _cache[13] || (_cache[13] = _createElementVNode("svg", {
                                        class: "gs-item-arrow",
                                        width: "14",
                                        height: "14",
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        stroke: "currentColor",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      }, [
                                        _createElementVNode("polyline", { points: "9 18 15 12 9 6" })
                                      ], -1))
                                    ], 42, _hoisted_19))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true)
                          ]))
                ]),
                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "gs-footer" }, [
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "↑↓"),
                    _createTextVNode(" navegar")
                  ]),
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "↵"),
                    _createTextVNode(" abrir")
                  ]),
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "Esc"),
                    _createTextVNode(" fechar")
                  ])
                ], -1))
              ])
            ], 32))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})